import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { registryRecordHref } from '../../../components/SharedRecord';
import { registryHref } from '../../Registries/registryHref';
import { PricingSheetSourceDrawer } from './PricingSheetSourceDrawer';
import { useVehicleCompanies, useVehicleConnections, useVehicleViewer } from './vehicleIdentityHooks';
export function vehicleConnectionHref(row, vehicleId, origin) {
    const context = `vehicle=${encodeURIComponent(vehicleId)}&returnTo=${encodeURIComponent(origin)}`;
    if (row.kind === 'program' && row.programId) return `/delivery/programs/${encodeURIComponent(row.programId)}?${context}`;
    if (row.kind === 'labor' || row.kind === 'rate') {
        const entryId = row.kind === 'rate' ? row.lcatEntryId : row.id;
        if (entryId) return registryHref({
            kind: 'LCAT_ENTRY',
            id: entryId
        }, origin);
        return `/registries?tab=lcats&${context}`;
    }
    // A source ID is a pricing generation, not a review change-set ID.
    // Open its actual artifact in the owning source viewer instead.
    if (row.kind === 'source') return null;
    if (row.kind === 'association' && row.companyId) return registryRecordHref('company', row.companyId, origin);
    return null;
}
export function VehicleConnectionsSection({ vehicleId, kind }) {
    const query = useVehicleConnections(vehicleId, kind);
    const viewer = useVehicleViewer();
    const companies = useVehicleCompanies();
    const [source, setSource] = useState(null);
    const location = useLocation();
    const origin = location.pathname + location.search;
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-2",
        "aria-label": `${kind} connections`,
        "data-testid": Testids.vehicleIdentity.connections,
        "data-kind": kind,
        children: [
            query.isPending ? /*#__PURE__*/ _jsxs("output", {
                children: [
                    "Loading ",
                    kind,
                    " connections…"
                ]
            }) : query.isError ? /*#__PURE__*/ _jsxs("div", {
                role: "alert",
                children: [
                    "Could not load ",
                    kind,
                    " connections.",
                    ' ',
                    /*#__PURE__*/ _jsxs(SpecraButton, {
                        onClick: ()=>void query.refetch(),
                        children: [
                            "Retry ",
                            kind
                        ]
                    })
                ]
            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs("p", {
                        children: [
                            query.data.length,
                            " visible loaded",
                            ' ',
                            kind === 'association' ? 'source associations (evidence rows, not companies or seats)' : `${kind} records`
                        ]
                    }),
                    query.data.length === 0 && /*#__PURE__*/ _jsxs("p", {
                        children: [
                            "No permitted ",
                            kind,
                            " records loaded. This does not establish that none exist."
                        ]
                    }),
                    query.data.map((r)=>{
                        const href = vehicleConnectionHref(r, vehicleId, origin);
                        return /*#__PURE__*/ _jsxs("div", {
                            className: "rounded border p-2",
                            "data-testid": Testids.vehicleIdentity.connectionRow,
                            "data-record-id": r.id,
                            children: [
                                r.kind === 'source' && r.artifactId ? /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    variant: "secondary",
                                    onClick: ()=>setSource(r),
                                    children: r.label || 'Open pricing source'
                                }) : href ? /*#__PURE__*/ _jsx(Link, {
                                    className: "text-primary underline",
                                    to: href,
                                    children: r.label || r.id
                                }) : r.label || r.id,
                                r.companyId && /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        ' ',
                                        "·",
                                        ' ',
                                        /*#__PURE__*/ _jsx(Link, {
                                            className: "text-primary underline",
                                            to: registryRecordHref('company', r.companyId, origin),
                                            children: companies.data?.find((c)=>c.id === r.companyId)?.name || `Company · ${r.companyId}`
                                        })
                                    ]
                                })
                            ]
                        }, `${r.kind}:${r.id}`);
                    })
                ]
            }),
            source?.artifactId && /*#__PURE__*/ _jsx(PricingSheetSourceDrawer, {
                isOpen: true,
                onClose: ()=>setSource(null),
                companyId: viewer.tenant,
                sourceDocumentId: source.artifactId,
                filename: source.label
            }),
            kind === 'association' && /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-muted-foreground",
                children: "Associated evidence only. A labor, rate or pricing-source reference does not establish holding or procurement eligibility."
            }),
            (kind === 'labor' || kind === 'rate') && /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-muted-foreground",
                children: "In Labor & Rates select this Vehicle in the Vehicle filter. That view does not yet restore filters from a link."
            })
        ]
    });
}
